import { memo, useCallback, useEffect, useState } from 'react';

import type { FilterDescriptor } from '@progress/kendo-data-query';
import type { GridFilterCellProps } from '@progress/kendo-react-grid';

import { formatDateForApiWithoutTimezone } from 'core/utils/date-serialization';

import type { DateRangeFilterValue } from '../../../DateRangeFilter';
import { DateRangeFilterButton } from './DateRangeFilterButton';

export interface DateRangeFilterCellProps extends GridFilterCellProps {
  /**
   * Title for the date filter
   */
  title?: string;
}

/**
 * Converts DateRangeFilterValue to individual FilterDescriptor objects
 */
function dateRangeToFilterDescriptors(field: string, value: DateRangeFilterValue): FilterDescriptor[] {
  const filters: FilterDescriptor[] = [];
  const now = new Date();

  let fromDate: Date | null = null;
  let toDate: Date | null = null;

  if (value.type === 'custom') {
    fromDate = value.fromDate || null;
    toDate = value.toDate || null;
  } else {
    // Calculate dates for relative types using local timezone
    switch (value.type) {
      case 'today':
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        toDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
        break;
      }
      case 'this-week': {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        fromDate = startOfWeek;
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      }
      case 'last-7-days':
        fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - 6);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'last-30-days':
        fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - 29);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'this-month':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'last-month':
        fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        toDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'this-year':
        fromDate = new Date(now.getFullYear(), 0, 1);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case 'last-year': {
        const lastYear = now.getFullYear() - 1;
        fromDate = new Date(lastYear, 0, 1);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(lastYear, 11, 31, 23, 59, 59, 999);
        break;
      }
      case 'last-90-days':
        fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - 89);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'last-365-days':
        fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - 364);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      // Add other cases as needed
      default:
        // For unsupported types, default to today
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }
  }

  if (fromDate) {
    filters.push({
      field,
      operator: 'gte',
      value: fromDate,
    });
  }

  if (toDate) {
    filters.push({
      field,
      operator: 'lte',
      value: toDate,
    });
  }

  return filters;
}

export const DateRangeFilterCell = memo<DateRangeFilterCellProps>(({ title, ...props }) => {
  const [currentValue, setCurrentValue] = useState<DateRangeFilterValue | null>(null);

  // Initialize from props.value if available
  useEffect(() => {
    if (props.value != null && !currentValue) {
      // The value might be a DateRangeFilterValue that was improperly serialized
      if (typeof props.value === 'object' && 'type' in props.value) {
        const value = props.value as DateRangeFilterValue;
        setCurrentValue(value);
      }
    }
  }, [props.value, currentValue]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterValue: DateRangeFilterValue) => {
      setCurrentValue(filterValue);

      if (props.onChange && props.field) {
        const filters = dateRangeToFilterDescriptors(props.field, filterValue);

        // For Kendo compatibility, we need to set this as a special serializable object
        // that can be detected and handled properly by the serialization layer
        const serializableRange = {
          __isDateRange: true,
          type: filterValue.type,
          fromDate: filterValue.fromDate ? formatDateForApiWithoutTimezone(filterValue.fromDate) : null,
          toDate: filterValue.toDate ? formatDateForApiWithoutTimezone(filterValue.toDate) : null,
          filters, // Include the actual filters for easy access
        };

        props.onChange({
          value: serializableRange,
          operator: 'eq',
          syntheticEvent: {} as React.SyntheticEvent,
        });
      }
    },
    [props],
  );

  // Handle clearing the filter
  const handleClear = useCallback(() => {
    setCurrentValue(null);
    if (props.onChange) {
      props.onChange({
        value: null,
        operator: 'eq',
        syntheticEvent: {} as React.SyntheticEvent,
      });
    }
  }, [props]);

  return <DateRangeFilterButton title={title || `Filter ${props.field || 'Date'}`} value={currentValue || undefined} onChange={handleFilterChange} />;
});

DateRangeFilterCell.displayName = 'DateRangeFilterCell';
